<template>
  <caption-header-skeleton v-if="status === 'loading'" :widget-size="widgetSize" :size="size" :has-shadow="hasShadow" :has-padding="hasPadding" :has-competition="hasCompetition" />
  <template v-else>
    <div
      :class="[
        'szw-flex szw-flex-col szw-rounded-lg szw-w-full szw-justify-between szw-items-center szw-h-[110px]',
        { 'szw-shadow-lg': hasShadow },
        { 'szw-p-1': hasPadding },
        colorSchema.wrapperBackground
      ]"
    >
      <!-- competition name -->
      <div class="szw-flex szw-justify-center szw-items-center szw-w-full szw-max-w-[768px] szw-h-[28px]" v-if="hasCompetition">
        <div class="szw-flex szw-w-full szw-h-full szw-overflow-hidden szw-items-center">
          <szw-text :text="headerData.competitionLabel" align="center" :classes="textClasses" />
        </div>
      </div>

      <!-- score board -->
      <div class="szw-w-full szw-h-full szw-flex szw-items-center szw-gap-[8px] szw-justify-center szw-max-w-[768px]">
        <!-- Left side (home) -->
        <div class="szw-flex szw-flex-1 szw-side" :class="dynamicTeamLogoPositionClasses('left')">
          <img v-if="headerData.home.logo?.medium" :class="widgetSize.img" :src="headerData.home.logo.medium" alt="logo" />
          <fallback-logo v-else :text="headerData.home.abbreviation" :class="[widgetSize.img, 'szw-text-text-icon-primary']" style="font-size: 20px" />
          <div :class="['szw-text-center', { 'szw-w-[64px]': size < 480 }, widgetSize.fontTeamName]">{{ dynamicTeamName(headerData.home) }}</div>
        </div>

        <!-- Middle section (fixed width) -->
        <div class="szw-w-[140px] szw-h-[78px] szw-flex szw-items-center szw-justify-center">
          <template v-if="headerData.upcoming">
            <div class="szw-uppercase szw-text-center szw-text-text-icon-primary szw-font-semibold">
              <div class="szw-text-xl">{{ getDate() }}</div>
              <div class="szw-text-lg szw-leading-[22px]">{{ formatTime() }}</div>
            </div>
          </template>
          <template v-else-if="headerData.formStatus">
            <div class="szw-flex szw-flex-col szw-uppercase szw-text-center szw-font-bold szw-gap-[4px] szw-justify-center szw-items-center">
              <div class="szw-grid szw-grid-cols-[1fr_auto_1fr] szw-items-center szw-w-full szw-gap-[4px] szw-text-[28px] szw-tracking-wider">
                <!-- Home score (left side) -->
                <div class="szw-text-right">{{ headerData.home.score }}</div>
                <div class="szw-w-[10px] szw-text-center">:</div>
                <!-- Away score (right side) -->
                <div class="szw-text-left">{{ headerData.away.score }}</div>
              </div>
              <div 
                class="szw-w-[104px] szw-h-[28px]  szw-flex szw-justify-center szw-items-center szw-rounded-[4px]"
                :style="{ background: `var(--szw-${headerData.status === 'W' ? 'win' : headerData.status === 'L' ? 'lose' : 'draw'})` }"
              >
                <span class="szw-font-medium szw-text-[12px] szw-px-[4px] szw-tracking-[4%] szw-uppercase szw-text-latest-text-icon-secondary szw-leading-[14px]">
                  {{ headerData.matchStatus }}
                </span>
              </div>
              <span class="szw-text-[10px] szw-leading-[12px] szw-text-text-icon-tertiary szw-font-medium">{{ getDate('D_num M_short YYYY') }}</span>
            </div>
          </template>
          <template v-else>
            <div class="szw-flex szw-flex-col szw-uppercase szw-text-center szw-font-bold szw-gap-[4px] szw-justify-center szw-items-center">
              <div class="szw-grid szw-grid-cols-[1fr_auto_1fr] szw-items-center szw-w-full szw-gap-[4px] szw-text-[28px] szw-tracking-wider">
                <!-- Home score (left side) -->
                <div class="szw-text-right">{{ headerData.home.score }}</div>
                <div class="szw-w-[10px] szw-text-center">:</div>
                <!-- Away score (right side) -->
                <div class="szw-text-left">{{ headerData.away.score }}</div>
              </div>
              <div
                v-if="headerData.isGameLive"
                class="szw-w-[104px] szw-h-[28px] szw-bg-label-color-live szw-flex szw-justify-center szw-items-center szw-rounded-[4px] szw-gap-[4px]"
              >
                <sprite sprite-id="live-indicator" color="var(--szw-text-icon-secondary)" class="szw-h-[26px] szw-mt-[1px]" />
                <span class="szw-font-semibold szw-text-sm szw-text-text-icon-secondary szw-px-[4px] szw-tracking-wide szw-capitalize">{{ loc('sm_live', langId) }}</span>
              </div>
              <div v-else class="szw-w-[104px] szw-h-[28px] szw-bg-label-color-final szw-flex szw-justify-center szw-items-center szw-rounded-[4px]">
                <sprite sprite-id="final-time" color="var(--szw-text-icon-primary)" class="szw-h-[20px]" />
                <span class="szw-font-semibold szw-text-sm szw-text-text-icon-primary szw-px-[4px] szw-tracking-wide szw-uppercase"
                  >{{ statusLabel }} {{ headerData.penaltiesScore }}</span
                >
              </div>
            </div>
          </template>
        </div>
        <!-- Right side (away) -->
        <div class="szw-flex szw-flex-1 szw-side" :class="dynamicTeamLogoPositionClasses('right')">
          <img v-if="headerData.away.logo?.medium" :class="widgetSize.img" :src="headerData.away.logo.medium" alt="logo" />
          <fallback-logo v-else :text="headerData.away.abbreviation" :class="[widgetSize.img, 'szw-text-text-icon-primary']" style="font-size: 20px" />
          <div :class="['szw-text-center', { 'szw-w-[64px]': size < 480 }, widgetSize.fontTeamName]">{{ dynamicTeamName(headerData.away) }}</div>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import { inject, computed } from 'vue'
import CaptionHeaderSkeleton from './Skeleton.vue'
import FallbackLogo from '@/__core__/components/FallbackLogo.vue'
import { formatDate } from '@/__core__/components/headers/common-header/CommonHeader.helpers'
import Sprite from '@/__core__/components/sprite/Sprite.vue'
import { loc } from '@/__core__/localization/localization'
import SzwText from '@/__core__/components/typography/text/Text.vue'

const props = defineProps({
  headerData: {
    type: Object,
    default: () => ({})
  },
  size: {
    type: Number,
    default: 320
  },
  hasShadow: {
    type: Boolean,
    default: true
  },
  hasPadding: {
    type: Boolean,
    default: true
  },
  hasCompetition: {
    type: Boolean,
    default: true
  }
})

const status = inject('status')
const langId = inject('langId', '')
const themeVersion = inject('themeVersion', 'current')

const colorSchema = computed(() => {
  if (themeVersion === 'latest') {
    return {
      wrapperBackground: 'szw-bg-latest-background'
    }
  }

  return {
    wrapperBackground: 'szw-bg-background-color-secondary'
  }
})

const statusLabel = computed(() => {
  const { matchStatus } = props.headerData

  let status = ''
  if (matchStatus === 'ended') {
    status = loc('sm_full_time_short', langId)
  } else if (matchStatus === 'aet') {
    status = loc('sm_aet', langId)
  } else if (matchStatus === 'ap') {
    status = loc('sm_ap', langId)
  } else {
    return status
  }

  return status
})
const widgetSize = computed(() => {
  const headerHeightSizes = {
    s: 'szw-min-h-[122px] szw-h-[122px]',
    m: 'szw-min-h-[128px] szw-h-[128px] szw-pt-[8px]',
    l: 'szw-min-h-[140px] szw-h-[140px] szw-pt-[8px]'
  }

  const imageSizes = {
    s: 'szw-w-[60px] szw-h-[60px]',
    m: 'szw-w-[64px] szw-h-[64px]',
    l: 'szw-w-[68px] szw-h-[68px]',
    xl: 'szw-w-[72px] szw-h-[72px]'
  }

  const fontSizes = {
    s: 'szw-text-[14px] szw-leading-[17px]',
    m: 'szw-text-[16px] szw-leading-[20px]',
    l: 'szw-text-[18px] szw-leading-[22px]',
    xl: 'szw-text-[22px] szw-leading-[26px]'
  }

  let conf

  if (props.size <= 480) {
    conf = { height: headerHeightSizes.s, font: fontSizes.s, fontTeamName: fontSizes.m, img: imageSizes.s }
  } else if (props.size > 480 && props.size < 640) {
    conf = { height: headerHeightSizes.m, font: fontSizes.s, fontTeamName: fontSizes.m, img: imageSizes.m }
  } else if (props.size >= 640 && props.size < 768) {
    conf = { height: headerHeightSizes.m, font: fontSizes.m, fontTeamName: fontSizes.m, img: imageSizes.m }
  } else {
    conf = { height: headerHeightSizes.l, font: fontSizes.m, fontTeamName: fontSizes.l, img: imageSizes.m }
  }

  return conf
})

const textClasses = computed(() => {
  const base = `${widgetSize.value.font} szw-font-normal`
  const statusClass = !props.headerData.formStatus
    ? 'szw-text-text-icon-tertiary'
    : 'szw-text-text-icon-primary szw-uppercase szw-text-[10px] szw-leading-[12px]' // fallback or alternative
  return `${base} ${statusClass}`
})

const dynamicTeamLogoPositionClasses = alignment => {
  if (props.size < 480) {
    return alignment === 'left' ? 'szw-flex-col szw-justify-center szw-items-end szw-gap-[4px]' : 'szw-flex-col szw-justify-center szw-items-start szw-gap-[4px]'
  } else {
    return alignment === 'left' ? 'szw-flex-row-reverse szw-justify-start szw-items-center szw-gap-[8px]' : 'szw-flex-row szw-justify-start szw-items-center szw-gap-[8px]'
  }
}

const dynamicTeamName = team => {
  if (props.size < 640) {
    return team.abbreviation
  } else {
    return team.name
  }
}

const getDate = (format = 'DD_num MM_short') => {
  const date = new Date(props.headerData.startTime)
  return formatDate(date, langId, format)
}

const formatTime = () => {
  const date = new Date(props.headerData.startTime)
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false // Specify 24-hour time format
  })
}
</script>
